import { PaymentResult, confirmPayment } from '@monei-js/components'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { DefaultNamespace, UseTranslationResponse } from 'react-i18next'

import { getDeviceInfoAsJsonString } from '../../../modules/mercadopago'
import { Address, CompleteUser } from '../../@types'
import { PromoCodeErrorCodes } from '../../components/PromoCode/types'
import { MERCADO_PAGO_API_PUBLIC_KEY } from '../../constants/config'
import { Summary, Symptom } from '../../context/cart'
import { isWeb } from '../../util/platform'
import { getToken } from '../auth'
import { CART_SESSION_KEY } from '../cart'
import { getPromoCodeErrorMessage } from '../discounts'
import { apiRequest, isSuccess, mercadoPagoRequest } from '../request'
import { type Error, Res } from '../request/make-request'

interface Card {
  id: string
  first_six_digits: string
  expiration_month: string
  expiration_year: string
  last_four_digits: string
}

interface GetCardTokenOptions {
  cardNumber: string
  cardHolder: string
  expireYear: number
  expireMonth: number
  securityCode: string
  identification?: { type: string; number: string }
}

interface GetSavedCardTokenOptions {
  cardId: string
  securityCode: string
}

/**
 * This function generates a unique identifier for the device using the MercadoPago script added in the index.html file.
 * Only works on web, since the script is not available in native. See apps/patient/web/utils.js and
 * https://www.mercadopago.com.ar/developers/en/docs/checkout-api/how-tos/improve-payment-approval#bookmark_implement_the_device_id_in_your_site
 */
function getDeviceSessionId() {
  if (typeof window === 'undefined') return

  return window.MP_DEVICE_SESSION_ID
}

/**
 * This function uses the native Android and iOS SDKs from MercadoPago to get a fingerprint of the device.
 * See apps/patient/modules/mercadopago and
 * https://www.mercadopago.com.ar/developers/en/docs/checkout-api/how-tos/improve-payment-approval#bookmark_implement_the_device_id_in_your_native_mobile_application
 */
function getNativeDeviceInfo() {
  if (isWeb()) {
    return null
  }

  const deviceInfoJsonString = getDeviceInfoAsJsonString()
  try {
    return JSON.parse(deviceInfoJsonString)
  } catch (error) {
    console.error('🔴 Error parsing MercadoPago device info', error, deviceInfoJsonString)
    return null
  }
}

export async function moneiConfirmPayment(
  paymentId: string,
  cardholderName: string,
  paymentToken?: string,
  email?: string
): Promise<PaymentResult | undefined> {
  try {
    if (!paymentToken) {
      throw new Error('Payment token not provided')
    }

    const result = await confirmPayment({
      paymentId,
      paymentToken,
      paymentMethod: { card: { cardholderName, ...(!!email && { cardholderEmail: email }) } },
    })

    if (result.status !== 'SUCCEEDED') {
      console.error('MONEI: Payment verification failed: ', result.statusMessage)
      throw new Error('Payment not authorized')
    }

    return result
  } catch (error: unknown) {
    console.error('MONEI: Error while confirming payment: ', error)
    throw new Error('Error trying to confirm payment')
  }
}

export function getIdentification(profile: CompleteUser | undefined) {
  if (!profile?.cpfData) return
  return { type: 'CPF', number: profile.cpfData.code }
}

export async function getCardToken({
  cardNumber,
  cardHolder,
  expireYear,
  expireMonth,
  securityCode,
  identification,
}: GetCardTokenOptions) {
  const searchParams = new URLSearchParams({
    public_key: MERCADO_PAGO_API_PUBLIC_KEY,
  })
  const url = '/card_tokens?' + searchParams.toString()

  const nativeDeviceInfo = getNativeDeviceInfo()

  return mercadoPagoRequest<Card>(url, {
    body: {
      card_number: cardNumber,
      cardholder: {
        name: cardHolder,
        identification,
      },
      security_code: securityCode,
      expiration_month: expireMonth,
      expiration_year: expireYear,
      ...(nativeDeviceInfo && { device: nativeDeviceInfo }),
    },
  })
}

export function getSavedCardToken({ cardId, securityCode }: GetSavedCardTokenOptions) {
  const searchParams = new URLSearchParams({
    public_key: MERCADO_PAGO_API_PUBLIC_KEY,
  })
  const url = '/card_tokens?' + searchParams.toString()

  const nativeDeviceInfo = getNativeDeviceInfo()

  return mercadoPagoRequest<Card>(url, {
    body: {
      cardId,
      securityCode,
      ...(nativeDeviceInfo && { device: nativeDeviceInfo }),
    },
  })
}

export async function deleteCard(cardId: string) {
  return apiRequest<{ workflowId: string }>(`/payment/card/${cardId}`, {
    method: 'DELETE',
    getToken,
  })
}

export async function cancelOrder(orderUuid: string, reason: string) {
  return apiRequest(`/order/${orderUuid}/cancel`, {
    method: 'PATCH',
    body: {
      reason,
    },
    getToken,
  })
}

export async function saveUserCard(cardToken: string) {
  return apiRequest(`/payment/card/`, {
    method: 'POST',
    body: {
      cardToken,
    },
    getToken,
  })
}

export enum PaymentMethodType {
  CARD_FIRST_PAYMENT = 'CARD_FIRST_PAYMENT',
}

export interface EncryptedCardInfo {
  brand: string
  checkoutAttemptId: string
  encryptedCardNumber: string
  encryptedExpiryMonth: string
  encryptedExpiryYear: string
  encryptedSecurityCode: string
  holderName: string
  type: string
}

export interface MoneiCardPaymentMethodDTO {
  card: {
    number?: string
    cvc?: string
    expMonth?: string
    expYear?: string
    cardholderName?: string
    cardholderEmail?: string
  }
}

export interface AdyenCheckoutState {
  isValid: boolean
  data: {
    paymentMethod: EncryptedCardInfo
  }
}

interface PaymentMethodInfo {
  paymentMethodDTO?: MoneiCardPaymentMethodDTO
  paymentMethodType: PaymentMethodType
  installments?: number
  cardToken?: string
  saveCard: boolean
  isSavedCard: boolean
}

export type PlaceOrderInput = {
  paymentMethodInfo?: PaymentMethodInfo
  cardToken?: string
  patientId?: number
  productId: number
  saveCard?: boolean
  symptoms?: Symptom[]
  summary?: Summary
  isSavedCard?: boolean
  referralCode?: string
  maskedCardNumber?: string
  cardType?: string
  discountName?: string
  promoCode?: string
  deviceSessionId?: string
  homeAddress?: Address
  billingAddress?: Address
  installments?: number
  sessionId?: string
}

export type PlaceOrderOutput = {
  workflowId: string
}

async function placeOrder({
  paymentMethodInfo,
  cardToken,
  patientId,
  productId,
  symptoms,
  summary,
  saveCard,
  isSavedCard = false,
  referralCode,
  maskedCardNumber,
  discountName,
  cardType,
  promoCode,
  homeAddress,
  billingAddress,
  installments,
  sessionId,
}: PlaceOrderInput): Promise<Res<PlaceOrderOutput>> {
  const cartSessionKey = await AsyncStorage.getItem(CART_SESSION_KEY)
  return apiRequest<PlaceOrderOutput>('/order', {
    body: {
      deviceSessionId: getDeviceSessionId(),
      paymentMethodInfo,
      cardToken,
      patientId,
      productId,
      symptoms,
      summary,
      saveCard,
      isSavedCard,
      referralCode,
      maskedCardNumber,
      cardType,
      discountName,
      promoCode,
      homeAddress,
      billingAddress,
      installments,
      cartSessionKey,
      sessionId,
    },
    getToken,
  })
}

export function getIsAMEX(cardNumber: string) {
  return /^3[47]\d{13}$/.test(cardNumber)
}

/* @see https://www.geeksforgeeks.org/how-to-validate-mastercard-number-using-regular-expression/ */
export function getIsMasterCard(cardNumber: string) {
  return /^5[1-5]\d{14}|^(222[1-9]|22[3-9]\\d|2[3-6]\\d{2}|27[0-1]\\d|2720)\d{12}$/.test(cardNumber)
}

export const getPaymentError = (
  t: UseTranslationResponse<DefaultNamespace>['t'],
  error?: Error
): ProcessPaymentError => {
  const { code, message } = error || {}

  if (message?.includes('OUT_OF_HOURS')) return { code, message: t('screens.paymentMethods.errors.outOfHours') }
  if (message?.includes('CLOSE')) return { code, message: t('screens.paymentMethods.errors.closed') }
  const promoCodeError = getPromoCodeErrorMessage(t, code as PromoCodeErrorCodes | undefined)
  if (promoCodeError) return { code, message: promoCodeError }
  if (code && ['SLOT_LOCK_EXPIRED', 'SLOT_LOCK_NOT_FOUND'].includes(code)) {
    return { code, message: t('screens.paymentMethods.errors.generic') }
  }
  return { code, message: t('screens.paymentMethods.errors.generic') }
}

type ProcessPaymentError = { code?: string; message: string }

interface ProcessPaymentProps {
  placeOrderData: PlaceOrderInput
  onErrorHandling: (error: ProcessPaymentError) => void
  onSuccess: (response: Res<PlaceOrderOutput>) => void
  t: UseTranslationResponse<DefaultNamespace>['t']
}

export const processPayment = async ({ placeOrderData, onErrorHandling, onSuccess, t }: ProcessPaymentProps) => {
  try {
    const placeOrderResponse = await placeOrder(placeOrderData)

    if (!isSuccess(placeOrderResponse)) {
      return onErrorHandling(getPaymentError(t, placeOrderResponse.error))
    }

    onSuccess(placeOrderResponse)
  } catch (error) {
    console.error('🔴 Error placing order', error)
    return onErrorHandling(getPaymentError(t))
  }
}
